// Upload a CSV export of the separate excess-fund bank account and match
// each transaction against the driver contributions payroll already logged
// (driver_excess_fund_ledger), so Janelle can confirm what actually landed
// against what was expected. See src/lib/insuranceExcessReconciliation.ts
// for the parsing/matching logic itself -- this is just the 3-step UI
// (upload -> map columns -> review & confirm matches) around it.
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, RefreshCw, Landmark, CheckCircle2, ArrowLeft } from "lucide-react";
import {
  parseCsvRows,
  normalizeDate,
  parseAmount,
  guessColumns,
  buildReconciliationRows,
  pickLedgerEntryForDriver,
  saveReconciliation,
  fetchUnreconciledContributions,
  fetchRecentBankTransactions,
  fetchBankTransactionStatusCounts,
  updateBankTransactionMatch,
  type ParsedBankRow,
  type ReconciliationRow,
  type ReconciliationStatus,
  type UnreconciledContribution,
  type BankTransactionRow,
} from "@/lib/insuranceExcessReconciliation";
import { formatDate, formatDateTime } from "@/lib/dates";

const IGNORE_VALUE = "__ignore__";
const NO_MATCH_VALUE = "__none__";

const fmtMoney = (n: number) => `$${n.toFixed(2)}`;

const STATUS_BADGE: Record<string, string> = {
  matched: "bg-green-800 text-green-200",
  unmatched: "bg-orange-800 text-orange-200",
  ignored: "bg-gray-700 text-gray-300",
};

const InsuranceExcessReconciliationTab: React.FC = () => {
  const [step, setStep] = useState<"upload" | "map" | "review">("upload");
  const [fileName, setFileName] = useState<string>("");
  const [rawRows, setRawRows] = useState<string[][]>([]);
  const [dateCol, setDateCol] = useState<string>("");
  const [amountCol, setAmountCol] = useState<string>("");
  const [refCol, setRefCol] = useState<string>("");

  const [driverOptions, setDriverOptions] = useState<{ driver_name: string; is_active: boolean }[]>([]);
  const [unreconciled, setUnreconciled] = useState<UnreconciledContribution[]>([]);
  const [reviewRows, setReviewRows] = useState<ReconciliationRow[]>([]);
  const [reviewTab, setReviewTab] = useState<ReconciliationStatus>("unmatched");
  const [saving, setSaving] = useState(false);

  const [recent, setRecent] = useState<BankTransactionRow[]>([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [recentTab, setRecentTab] = useState<ReconciliationStatus>("unmatched");
  const [recentCounts, setRecentCounts] = useState<Record<ReconciliationStatus, number>>({ matched: 0, unmatched: 0, ignored: 0 });
  const [savingRecentId, setSavingRecentId] = useState<string | null>(null);
  const [recentSearch, setRecentSearch] = useState("");

  const loadRecent = async (status: ReconciliationStatus = recentTab) => {
    setLoadingRecent(true);
    const [rows, counts] = await Promise.all([fetchRecentBankTransactions(status), fetchBankTransactionStatusCounts()]);
    setRecent(rows);
    setRecentCounts(counts);
    setLoadingRecent(false);
  };

  // Driver options are needed for the Recent Bank Transactions edit dropdown
  // too, which is visible before any CSV is ever uploaded -- load once here
  // rather than only after proceedToReview.
  const loadDriverOptions = async () => {
    const { data } = await supabase.from("drivers").select("driver_name, is_active").order("driver_name");
    setDriverOptions((data || []) as { driver_name: string; is_active: boolean }[]);
  };

  useEffect(() => {
    loadRecent("unmatched");
    loadDriverOptions();
  }, []);

  const switchRecentTab = (s: ReconciliationStatus) => {
    setRecentTab(s);
    loadRecent(s);
  };

  const header = rawRows[0] || [];
  const dataRows = rawRows.slice(1);

  const handleFile = async (file: File) => {
    setFileName(file.name);
    const text = await file.text();
    const parsed = parseCsvRows(text);
    if (parsed.length < 2) {
      toast({ title: "Empty file", description: "That CSV doesn't have any data rows.", variant: "destructive" });
      return;
    }
    setRawRows(parsed);
    const guess = guessColumns(parsed[0]);
    setDateCol(guess.date >= 0 ? String(guess.date) : "");
    setAmountCol(guess.amount >= 0 ? String(guess.amount) : "");
    setRefCol(guess.reference >= 0 ? String(guess.reference) : "");
    setStep("map");
  };

  const proceedToReview = async () => {
    if (dateCol === "" || amountCol === "" || refCol === "") {
      toast({ title: "Pick all three columns", description: "Date, Amount and Reference all need a column.", variant: "destructive" });
      return;
    }
    const dCol = Number(dateCol);
    const aCol = Number(amountCol);
    const rCol = Number(refCol);

    const parsed: ParsedBankRow[] = [];
    for (const row of dataRows) {
      const date = normalizeDate(row[dCol] || "");
      const amount = parseAmount(row[aCol] || "");
      const reference = (row[rCol] || "").trim();
      if (!date || amount == null || amount <= 0) continue; // skip debits/unparsable rows -- this account only receives
      parsed.push({ date, amount, reference });
    }
    if (parsed.length === 0) {
      toast({ title: "No usable rows", description: "Couldn't parse any valid credit rows with those columns.", variant: "destructive" });
      return;
    }

    // Deliberately not filtered to active-only -- a bank statement covers
    // past weeks, and someone who's since left still needs to be matchable
    // against their old contributions. Matching a row here only sets
    // matched_driver_name/matched_ledger_entry_id, it never touches
    // is_active, so there's no need to temporarily reactivate anyone.
    const [{ data: drivers }, unreconciledContributions] = await Promise.all([
      supabase.from("drivers").select("driver_name, is_active").order("driver_name"),
      fetchUnreconciledContributions(),
    ]);
    const options = (drivers || []) as { driver_name: string; is_active: boolean }[];
    setDriverOptions(options);
    setUnreconciled(unreconciledContributions);
    setReviewRows(buildReconciliationRows(parsed, options.map((d) => d.driver_name), unreconciledContributions));
    setReviewTab("unmatched");
    setStep("review");
  };

  /** Pure single-row update, reused for both the row the user actually
   *  clicked and (when confirmed) every sibling row sharing its reference. */
  const applyDriverMatch = (rows: ReconciliationRow[], idx: number, value: string): ReconciliationRow[] => {
    const claimed = new Set(
      rows.filter((r, i) => i !== idx && r.matchedLedgerEntryId).map((r) => r.matchedLedgerEntryId as string)
    );
    const next = [...rows];
    const row = next[idx];
    if (value === IGNORE_VALUE) {
      next[idx] = { ...row, status: "ignored", matchedDriverName: null, matchedLedgerEntryId: null };
    } else if (value === NO_MATCH_VALUE) {
      next[idx] = { ...row, status: "unmatched", matchedDriverName: null, matchedLedgerEntryId: null };
    } else {
      // Picking a driver is what "matched" means -- a linkable ledger entry
      // is a bonus (flips bank_reconciled on save), not a requirement.
      const entry = pickLedgerEntryForDriver(value, row.amount, unreconciled, claimed);
      next[idx] = {
        ...row,
        matchedDriverName: value,
        matchedLedgerEntryId: entry?.id || null,
        status: "matched",
      };
    }
    return next;
  };

  const updateRowDriver = (idx: number, value: string) => {
    const reference = reviewRows[idx]?.reference;
    setReviewRows((prev) => applyDriverMatch(prev, idx, value));

    // The same reference code (e.g. "INS EX MA") recurs every week for the
    // same driver -- offer to apply this exact choice (a driver match, or
    // an explicit Ignore) to every other row sharing it, so one decision
    // clears a whole run instead of clicking through each week by hand.
    if (value !== NO_MATCH_VALUE && reference) {
      const siblingIdxs = reviewRows
        .map((r, i) => ({ r, i }))
        .filter(
          ({ r, i }) =>
            i !== idx &&
            r.reference === reference &&
            (value === IGNORE_VALUE ? r.status !== "ignored" : r.matchedDriverName !== value)
        )
        .map(({ i }) => i);
      if (siblingIdxs.length > 0) {
        const label = value === IGNORE_VALUE ? "Ignore" : value;
        const proceed = window.confirm(
          `Apply "${label}" to the other ${siblingIdxs.length} row${siblingIdxs.length !== 1 ? "s" : ""} with reference "${reference}" too?`
        );
        if (proceed) {
          setReviewRows((prev) => {
            let next = prev;
            for (const i of siblingIdxs) next = applyDriverMatch(next, i, value);
            return next;
          });
        }
      }
    }
  };

  const summary = useMemo(() => {
    const matched = reviewRows.filter((r) => r.status === "matched").length;
    const unmatched = reviewRows.filter((r) => r.status === "unmatched").length;
    const ignored = reviewRows.filter((r) => r.status === "ignored").length;
    return { matched, unmatched, ignored };
  }, [reviewRows]);

  const confirmSave = async () => {
    setSaving(true);
    try {
      const result = await saveReconciliation(reviewRows);
      toast({
        title: "Reconciliation saved",
        description: `${result.matched} matched, ${result.unmatched} left unmatched, ${result.ignored} ignored.`,
      });
      setStep("upload");
      setRawRows([]);
      setFileName("");
      setReviewRows([]);
      await loadRecent();
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const updateRecentDriver = async (transaction: BankTransactionRow, value: string) => {
    setSavingRecentId(transaction.id);
    try {
      const driverName = value === IGNORE_VALUE || value === NO_MATCH_VALUE ? null : value;
      const status: ReconciliationStatus = value === IGNORE_VALUE ? "ignored" : value === NO_MATCH_VALUE ? "unmatched" : "matched";
      await updateBankTransactionMatch(transaction, driverName, status);
      await loadRecent();
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setSavingRecentId(null);
    }
  };

  const colOptions = header.map((h, i) => ({ value: String(i), label: h || `Column ${i + 1}` }));

  const filteredRecent = useMemo(() => {
    const q = recentSearch.trim().toLowerCase();
    if (!q) return recent;
    return recent.filter(
      (r) => (r.matched_driver_name || "").toLowerCase().includes(q) || (r.reference || "").toLowerCase().includes(q)
    );
  }, [recent, recentSearch]);

  return (
    <div className="space-y-6">
      <Card className="shadow-lg bg-card border border-border">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-xl text-primary flex items-center gap-2">
            <Landmark className="w-5 h-5" />
            Bank Reconciliation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-muted-foreground">
            Upload a CSV export of the excess fund's bank account. Each transaction gets matched against a driver's
            outstanding weekly contribution -- confirm or correct the matches, then save.
          </p>

          {step === "upload" && (
            <div className="rounded-lg border border-dashed border-border p-8 text-center space-y-3">
              <Upload className="w-8 h-8 mx-auto text-muted-foreground" />
              <div>
                <Label htmlFor="excess-bank-csv" className="cursor-pointer text-yellow-500 hover:underline">
                  Choose a CSV file
                </Label>
                <input
                  id="excess-bank-csv"
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFile(f);
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground">Exported directly from your bank -- any column order is fine.</p>
            </div>
          )}

          {step === "map" && (
            <div className="space-y-4">
              <p className="text-sm text-foreground">
                <span className="font-medium">{fileName}</span> -- {dataRows.length} row{dataRows.length !== 1 ? "s" : ""} found.
                Confirm which column is which.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-foreground text-sm">Date Column</Label>
                  <Select value={dateCol} onValueChange={setDateCol}>
                    <SelectTrigger className="dark-input"><SelectValue placeholder="Select column..." /></SelectTrigger>
                    <SelectContent>
                      {colOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-foreground text-sm">Amount Column</Label>
                  <Select value={amountCol} onValueChange={setAmountCol}>
                    <SelectTrigger className="dark-input"><SelectValue placeholder="Select column..." /></SelectTrigger>
                    <SelectContent>
                      {colOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-foreground text-sm">Reference Column</Label>
                  <Select value={refCol} onValueChange={setRefCol}>
                    <SelectTrigger className="dark-input"><SelectValue placeholder="Select column..." /></SelectTrigger>
                    <SelectContent>
                      {colOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {dataRows.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Reference</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dataRows.slice(0, 5).map((row, i) => (
                        <TableRow key={i}>
                          <TableCell>{dateCol !== "" ? row[Number(dateCol)] : "--"}</TableCell>
                          <TableCell>{amountCol !== "" ? row[Number(amountCol)] : "--"}</TableCell>
                          <TableCell>{refCol !== "" ? row[Number(refCol)] : "--"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setStep("upload")}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={proceedToReview} className="golden-button">
                  Continue
                </Button>
              </div>
            </div>
          )}

          {step === "review" && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 text-sm">
                {(["matched", "unmatched", "ignored"] as ReconciliationStatus[]).map((s) => (
                  <button key={s} type="button" onClick={() => setReviewTab(s)}>
                    <Badge
                      className={`${STATUS_BADGE[s]} cursor-pointer ${reviewTab === s ? "ring-2 ring-offset-1 ring-offset-background ring-yellow-500" : ""}`}
                    >
                      {summary[s]} {s}
                    </Badge>
                  </button>
                ))}
              </div>

              <div className="overflow-x-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Matched Driver</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reviewRows
                      .map((row, i) => ({ row, i }))
                      .filter(({ row }) => row.status === reviewTab)
                      .map(({ row, i }) => (
                      <TableRow key={i}>
                        <TableCell>{formatDate(row.date)}</TableCell>
                        <TableCell>{fmtMoney(row.amount)}</TableCell>
                        <TableCell className="max-w-[220px] truncate" title={row.reference}>{row.reference}</TableCell>
                        <TableCell>
                          <Select value={row.matchedDriverName || (row.status === "ignored" ? IGNORE_VALUE : NO_MATCH_VALUE)} onValueChange={(v) => updateRowDriver(i, v)}>
                            <SelectTrigger className="dark-input w-56"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NO_MATCH_VALUE}>-- No match --</SelectItem>
                              <SelectItem value={IGNORE_VALUE}>Ignore this row</SelectItem>
                              {driverOptions.map((d) => (
                                <SelectItem key={d.driver_name} value={d.driver_name}>
                                  {d.driver_name}{!d.is_active ? " (inactive)" : ""}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_BADGE[row.status]}>{row.status}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {reviewRows.filter((r) => r.status === reviewTab).length === 0 && (
                  <p className="text-sm text-muted-foreground italic text-center py-6">No {reviewTab} rows.</p>
                )}
              </div>

              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setStep("map")} disabled={saving}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={confirmSave} disabled={saving} className="golden-button">
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : "Confirm Reconciliation"}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-lg bg-card border border-border">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-lg text-primary">Bank Transactions</CardTitle>
          <Button variant="outline" size="sm" onClick={() => loadRecent()} disabled={loadingRecent}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Every transaction ever uploaded, across every statement -- pick up manual matching here for anything left
            over from a previous upload.
          </p>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {(["matched", "unmatched", "ignored"] as ReconciliationStatus[]).map((s) => (
              <button key={s} type="button" onClick={() => switchRecentTab(s)}>
                <Badge
                  className={`${STATUS_BADGE[s]} cursor-pointer ${recentTab === s ? "ring-2 ring-offset-1 ring-offset-background ring-yellow-500" : ""}`}
                >
                  {recentCounts[s]} {s}
                </Badge>
              </button>
            ))}
            <Input
              value={recentSearch}
              onChange={(e) => setRecentSearch(e.target.value)}
              placeholder="Find a driver or reference..."
              className="dark-input ml-auto w-64"
            />
          </div>
          {loadingRecent ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : filteredRecent.length === 0 ? (
            <p className="text-sm text-muted-foreground italic text-center py-6">
              {recentSearch ? `No ${recentTab} transactions match "${recentSearch}".` : `No ${recentTab} transactions.`}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Driver</TableHead>
                    <TableHead>Uploaded</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRecent.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{formatDate(r.transaction_date)}</TableCell>
                      <TableCell>{fmtMoney(r.amount)}</TableCell>
                      <TableCell className="max-w-[220px] truncate" title={r.reference || ""}>{r.reference}</TableCell>
                      <TableCell>
                        <Select
                          value={r.matched_driver_name || (r.status === "ignored" ? IGNORE_VALUE : NO_MATCH_VALUE)}
                          onValueChange={(v) => updateRecentDriver(r, v)}
                          disabled={savingRecentId === r.id}
                        >
                          <SelectTrigger className="dark-input w-56"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NO_MATCH_VALUE}>-- No match --</SelectItem>
                            <SelectItem value={IGNORE_VALUE}>Ignore this row</SelectItem>
                            {driverOptions.map((d) => (
                              <SelectItem key={d.driver_name} value={d.driver_name}>
                                {d.driver_name}{!d.is_active ? " (inactive)" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>{formatDateTime(r.uploaded_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InsuranceExcessReconciliationTab;
